import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    // Check if it's an admin endpoint
    if (config.url?.includes('/admin/') && !config.url?.includes('/admin/login')) {
        const adminToken = localStorage.getItem('hmc_admin_token');
        if (adminToken) {
            config.headers.Authorization = `Bearer ${adminToken}`;
        }
    } else {
        const token = localStorage.getItem('hmc_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
    }
    return config;
});

// Handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            const isAdminRoute = error.config?.url?.includes('/admin/');
            if (isAdminRoute) {
                localStorage.removeItem('hmc_admin_token');
                localStorage.removeItem('hmc_admin');
                if (window.location.pathname.startsWith('/admin')) {
                    window.location.href = '/admin';
                }
            } else {
                localStorage.removeItem('hmc_token');
                localStorage.removeItem('hmc_advertiser');
                if (!window.location.pathname.startsWith('/admin')) {
                    window.location.href = '/advertiser/login';
                }
            }
        }
        return Promise.reject(error);
    }
);

// Categories
export const getCategories = () => api.get('/categories');
export const getCategory = (slug) => api.get(`/categories/${slug}`);

// User Email
export const collectEmail = (email) => api.post('/users/email', { email });
export const trackActivity = (email, page) => api.post(`/users/activity?email=${email}&page=${page}`);

// Auth
export const registerAdvertiser = (data) => api.post('/auth/register', data);
export const loginAdvertiser = (data) => api.post('/auth/login', data);
export const getCurrentUser = () => api.get('/auth/me');
export const updateProfile = (data) => api.put('/auth/profile', data);

// Password Reset
export const forgotPassword = (email) => api.post('/auth/forgot-password', { email });
export const resetPassword = (token, new_password) => api.post('/auth/reset-password', { token, new_password });
export const verifyResetToken = (token) => api.get(`/auth/verify-reset-token/${token}`);

// Public Advertiser Profile
export const getAdvertiserProfile = (advertiserId) => api.get(`/advertiser/profile/${advertiserId}`);

// Favorites
export const addFavorite = (listingId) => api.post(`/favorites/${listingId}`);
export const removeFavorite = (listingId) => api.delete(`/favorites/${listingId}`);
export const getFavorites = () => api.get('/favorites');
export const checkFavorite = (listingId) => api.get(`/favorites/check/${listingId}`);

// Public Listings
export const getListings = (params) => api.get('/listings', { params });
export const getFeaturedListings = (limit = 8) => api.get(`/listings/featured?limit=${limit}`);
export const getListing = (id) => api.get(`/listings/${id}`);

// Advertiser Listings
export const createListing = (data) => api.post('/advertiser/listings', data);
export const getAdvertiserListings = () => api.get('/advertiser/listings');
export const updateListing = (id, data) => api.put(`/advertiser/listings/${id}`, data);
export const updateListingStatus = (id, status) => api.put(`/advertiser/listings/${id}/status?status=${status}`);
export const deleteListing = (id) => api.delete(`/advertiser/listings/${id}`);

// Stats
export const getStats = () => api.get('/stats');
export const getAdvertiserStats = () => api.get('/advertiser/stats');

// Contact
export const submitContact = (data) => api.post('/contact', data);

// ================== ADMIN API ==================
export const adminLogin = (data) => api.post('/admin/login', data);
export const getAdminProfile = () => api.get('/admin/me');
export const getAdminStats = () => api.get('/admin/stats');

// Admin - Listings
export const adminGetListings = (params) => api.get('/admin/listings', { params });
export const adminUpdateListing = (id, data) => api.put(`/admin/listings/${id}`, data);
export const adminDeleteListing = (id) => api.delete(`/admin/listings/${id}`);

// Admin - Advertisers
export const adminGetAdvertisers = (params) => api.get('/admin/advertisers', { params });
export const adminUpdateAdvertiser = (id, data) => api.put(`/admin/advertisers/${id}`, data);
export const adminDeleteAdvertiser = (id) => api.delete(`/admin/advertisers/${id}`);

// Admin - Users
export const adminGetUsers = (params) => api.get('/admin/users', { params });
export const adminDeleteUser = (email) => api.delete(`/admin/users/${encodeURIComponent(email)}`);

// Admin - Contacts
export const adminGetContacts = (params) => api.get('/admin/contacts', { params });
export const adminDeleteContact = (id) => api.delete(`/admin/contacts/${id}`);

// Admin - Messages
export const adminGetMessages = () => api.get('/admin/messages');
export const adminGetConversation = (id) => api.get(`/admin/messages/${id}`);
export const adminSendMessage = (data) => api.post('/admin/messages', data);
export const adminReplyMessage = (conversationId, data) => api.post(`/admin/messages/${conversationId}/reply`, data);

// Admin - Announcements
export const adminGetAnnouncements = () => api.get('/admin/announcements');
export const adminCreateAnnouncement = (data) => api.post('/admin/announcements', data);
export const adminUpdateAnnouncement = (id, data) => api.put(`/admin/announcements/${id}`, data);
export const adminDeleteAnnouncement = (id) => api.delete(`/admin/announcements/${id}`);

// Advertiser - Messages
export const getAdvertiserMessages = () => api.get('/advertiser/messages');
export const getAdvertiserConversation = (id) => api.get(`/advertiser/messages/${id}`);
export const advertiserReplyMessage = (conversationId, data) => api.post(`/advertiser/messages/${conversationId}/reply`, data);
export const getAdvertiserUnreadCount = () => api.get('/advertiser/messages/unread-count');

// Advertiser - Announcements
export const getAdvertiserAnnouncements = () => api.get('/advertiser/announcements');

// Public Announcements
export const getPublicAnnouncements = () => api.get('/announcements');

// Admin - Email Campaigns
export const adminGetCampaigns = () => api.get('/admin/campaigns');
export const adminCreateCampaign = (data) => api.post('/admin/campaigns', data);
export const adminGetCampaign = (id) => api.get(`/admin/campaigns/${id}`);
export const adminDeleteCampaign = (id) => api.delete(`/admin/campaigns/${id}`);

// Admin - Sales Team
export const adminGetSalesReps = () => api.get('/admin/sales-reps');
export const adminCreateSalesRep = (data) => api.post('/admin/sales-reps', data);
export const adminUpdateSalesRep = (id, data) => api.put(`/admin/sales-reps/${id}`, data);
export const adminDeleteSalesRep = (id) => api.delete(`/admin/sales-reps/${id}`);
export const adminAssignSalesRep = (advertiserId, salesRepId) => api.put(`/admin/advertisers/${advertiserId}/assign-sales-rep${salesRepId ? `?sales_rep_id=${salesRepId}` : ''}`);
export const adminGetCommissionSummary = () => api.get('/admin/sales-commission-summary');

// Admin - Categories
export const adminGetCategoryColors = () => api.get('/admin/category-colors');
export const adminGetCategories = () => api.get('/admin/categories');
export const adminCreateCategory = (data) => api.post('/admin/categories', data);
export const adminUpdateCategory = (slug, data) => api.put(`/admin/categories/${slug}`, data);
export const adminDeleteCategory = (slug) => api.delete(`/admin/categories/${slug}`);

// Admin - Subcategories
export const adminAddSubcategory = (categorySlug, data) => api.post(`/admin/categories/${categorySlug}/subcategories`, data);
export const adminUpdateSubcategory = (categorySlug, subcatSlug, data) => api.put(`/admin/categories/${categorySlug}/subcategories/${subcatSlug}`, data);
export const adminDeleteSubcategory = (categorySlug, subcatSlug) => api.delete(`/admin/categories/${categorySlug}/subcategories/${subcatSlug}`);

export default api;
